import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, FileText, Sparkles, Loader2, Trash2, ArrowRight,
  ArrowLeft, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const CAMERA_MOVEMENTS = [
  "static", "pan", "tilt", "dolly", "handheld", "crane", "drone",
  "steadicam", "gimbal", "track",
];

const MAX_MB = 5;

export default function ShotListImportDialog({ open, onOpenChange, projectId, onImported }) {
  const [tab, setTab]         = useState("file");    // file | text
  const [step, setStep]       = useState("input");   // input | preview | done
  const [file, setFile]       = useState(null);
  const [text, setText]       = useState("");
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [rows, setRows]       = useState([]);
  const fileRef = useRef(null);

  const reset = () => {
    setTab("file"); setStep("input"); setFile(null); setText("");
    setRows([]); setParsing(false); setCommitting(false);
  };

  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  async function parse() {
    if (tab === "file" && !file) {
      toast.error("Choose an .xlsx or .pdf file first"); return;
    }
    if (tab === "text" && !text.trim()) {
      toast.error("Paste your shot list text first"); return;
    }
    if (file && file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File must be under ${MAX_MB} MB`); return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      if (tab === "file") fd.append("file", file);
      else fd.append("text", text);
      const { data } = await api.post(
        `/projects/${projectId}/shots/import/parse`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      if (data.count === 0) {
        toast.error("Gemini couldn't find any shots — check the source content and try again");
        return;
      }
      setRows(data.shots.map((s, i) => ({ ...s, _key: `p-${i}-${Date.now()}` })));
      setStep("preview");
      toast.success(`Parsed ${data.count} shot${data.count === 1 ? "" : "s"} — review before importing`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Parse failed");
    } finally { setParsing(false); }
  }

  async function commit() {
    setCommitting(true);
    try {
      // strip our internal _key field before sending
      const payload = { shots: rows.map(({ _key, ...s }) => s) };
      const { data } = await api.post(
        `/projects/${projectId}/shots/import/commit`,
        payload,
        { timeout: 60000 },
      );
      toast.success(`Imported ${data.inserted} shots`);
      setStep("done");
      if (onImported) onImported();
      setTimeout(() => close(false), 900);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setCommitting(false); }
  }

  const updateRow = (key, patch) =>
    setRows(rs => rs.map(r => (r._key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) =>
    setRows(rs => rs.filter(r => r._key !== key));

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="shot-import-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <Sparkles size={16} className="text-fuchsia-400" />
            Import Shot List
          </DialogTitle>
          <DialogDescription className="text-white/55">
            {step === "input"   && "Upload an Excel/PDF, or paste plain text. Gemini 3 Flash reads it and structures the shots."}
            {step === "preview" && "Review the parsed rows below. Fix any misread field or delete rows before committing."}
            {step === "done"    && "Import complete."}
          </DialogDescription>
        </DialogHeader>

        {/* STEP: INPUT */}
        {step === "input" && (
          <div className="flex flex-col gap-4 overflow-y-auto">
            <div className="flex rounded-full border border-white/10 p-1 self-start">
              {[
                ["file", "Upload File", Upload],
                ["text", "Paste Text", FileText],
              ].map(([k, l, Icon]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] rounded-full inline-flex items-center gap-1.5 ${
                    tab === k ? "bg-white/15 text-white" : "text-white/50 hover:text-white/70"
                  }`}
                  data-testid={`shot-import-tab-${k}`}>
                  <Icon size={11} /> {l}
                </button>
              ))}
            </div>

            {tab === "file" && (
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                  data-testid="shot-import-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full rounded-2xl border-2 border-dashed border-white/15 hover:border-white/30 transition p-10 text-center flex flex-col items-center gap-3"
                  data-testid="shot-import-file-drop">
                  <Upload size={28} className="text-white/45" />
                  <div className="text-sm font-medium">
                    {file ? file.name : "Choose an .xlsx or .pdf file"}
                  </div>
                  <div className="text-[11px] text-white/45">
                    {file
                      ? `${(file.size / 1024).toFixed(1)} KB · click to change`
                      : `Max ${MAX_MB} MB · Excel or PDF only`}
                  </div>
                </button>
              </div>
            )}

            {tab === "text" && (
              <div>
                <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                  Paste shot list text
                </Label>
                <Textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder={"Scene 1 - Rooftop\nShot 1: Wide of skyline. 24mm, static. Day 1.\nShot 1A: Push in on hero, dolly, 50mm. Day 1.\n\nScene 2 - Cafe\n..."}
                  className="mt-2 bg-black/40 border-white/15 font-mono text-xs min-h-[280px]"
                  data-testid="shot-import-text-input" />
                <div className="mt-1 text-[10px] text-white/40">
                  Emailed shot list · exported CSV · notes app — all work. Gemini figures out the structure.
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP: PREVIEW */}
        {step === "preview" && (
          <div className="flex-1 overflow-y-auto space-y-2" data-testid="shot-import-preview">
            <div className="sticky top-0 bg-[#0c0c0e] pb-2 text-[10px] uppercase tracking-[0.22em] text-white/55 grid grid-cols-12 gap-2 pr-2">
              <div className="col-span-1">Scene</div>
              <div className="col-span-1">Shot</div>
              <div className="col-span-4">Description</div>
              <div className="col-span-1">Lens</div>
              <div className="col-span-2">Movement</div>
              <div className="col-span-2">Shoot Day</div>
              <div className="col-span-1"></div>
            </div>
            {rows.map(r => (
              <div key={r._key}
                className="grid grid-cols-12 gap-2 items-center rounded-lg border border-white/5 hover:border-white/20 p-2"
                data-testid={`shot-import-row-${r._key}`}>
                <Input value={r.scene_number}
                  onChange={e => updateRow(r._key, { scene_number: e.target.value })}
                  className="col-span-1 h-8 bg-black/40 border-white/15 text-xs" />
                <Input value={r.shot_number}
                  onChange={e => updateRow(r._key, { shot_number: e.target.value })}
                  className="col-span-1 h-8 bg-black/40 border-white/15 text-xs" />
                <Input value={r.description}
                  onChange={e => updateRow(r._key, { description: e.target.value })}
                  className="col-span-4 h-8 bg-black/40 border-white/15 text-xs" />
                <Input value={r.lens}
                  onChange={e => updateRow(r._key, { lens: e.target.value })}
                  className="col-span-1 h-8 bg-black/40 border-white/15 text-xs" />
                <div className="col-span-2">
                  <Select value={r.camera_movement}
                    onValueChange={v => updateRow(r._key, { camera_movement: v })}>
                    <SelectTrigger className="h-8 bg-black/40 border-white/15 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CAMERA_MOVEMENTS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Input value={r.shoot_day}
                  onChange={e => updateRow(r._key, { shoot_day: e.target.value })}
                  className="col-span-2 h-8 bg-black/40 border-white/15 text-xs"
                  placeholder="—" />
                <button onClick={() => removeRow(r._key)}
                  className="col-span-1 justify-self-end p-1.5 text-white/40 hover:text-red-400"
                  data-testid={`shot-import-remove-${r._key}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            {rows.length === 0 && (
              <div className="text-center py-10 text-sm text-white/45" data-testid="shot-import-empty">
                All rows removed. Go back or close.
              </div>
            )}
          </div>
        )}

        {/* STEP: DONE */}
        {step === "done" && (
          <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="shot-import-done">
            <CheckCircle2 size={40} className="text-emerald-400" />
            <div className="text-sm text-white/85">Shots imported into the project</div>
          </div>
        )}

        <DialogFooter className="shrink-0 pt-2 border-t border-white/5">
          {step === "input" && (
            <>
              <Button variant="ghost" onClick={() => close(false)} data-testid="shot-import-cancel">Cancel</Button>
              <Button onClick={parse} disabled={parsing}
                className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
                data-testid="shot-import-parse">
                {parsing ? (<><Loader2 size={12} className="animate-spin mr-2" /> Parsing…</>)
                         : (<>Parse with Gemini <ArrowRight size={12} className="ml-2" /></>)}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="ghost" onClick={() => setStep("input")} data-testid="shot-import-back">
                <ArrowLeft size={12} className="mr-1" /> Back
              </Button>
              <div className="flex-1 text-[10px] uppercase tracking-[0.22em] text-white/55 self-center">
                {rows.length} shot{rows.length === 1 ? "" : "s"} ready
              </div>
              <Button onClick={commit} disabled={committing || rows.length === 0}
                className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
                data-testid="shot-import-commit">
                {committing ? (<><Loader2 size={12} className="animate-spin mr-2" /> Importing…</>)
                            : (<>Import {rows.length} Shot{rows.length === 1 ? "" : "s"}</>)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
